/**
 * @module @app/shared/patterns/result
 * @description Result pattern.
 */

type Ok<T> = { readonly tag: symbol; value: T };
type Err<E> = { readonly tag: symbol; value: E };
export type Result<T, E> = Ok<T> | Err<E>;

/**
 * @description Utility tag to mark a result as a success.
 */
const ResultTagOk = Symbol.for("Ok");

/**
 * @description Utility tag to mark a result as a failure.
 */
const ResultTagErr = Symbol.for("Err");

/**
 * @description Create a Result that represent a successful operation.
 * @param value A value to wrap in a successful Result.
 * @returns
 */
export const Ok = <T>(value: T): Result<T, never> => ({ tag: ResultTagOk, value });

/**
 * @description Create a Result that represent a failed operation.
 * @param value A value to wrap in a failed Result.
 * @returns
 */
export const Err = <E>(value: E): Result<never, E> => ({ tag: ResultTagErr, value });

/**
 * Check if a Result is a successful one.
 * @param result
 * @returns
 */
export const isResultOk = <T, E>(result: Result<T, E>): result is Ok<T> =>
  result.tag === ResultTagOk;

/**
 * @description Check if a Result is a failed one.
 * @param result
 * @returns
 */
export const isResultErr = <T, E>(result: Result<T, E>): result is Err<E> =>
  result.tag === ResultTagErr;
